import circle from '@turf/circle';

import SearchUI from './ui';
import MaxZoomLevels from './zoom-levels';

import { getLanguage } from '../user/device-info';
import { validateURLString } from '../url/validate';
import { featureResponseStub } from './stubs';
import { removeRadius } from './events/radius';
import { addRadiusToMap } from './radius';
import { handleSearch, search } from './search-form';
import { assetURL, updateURLs, urlWithLanguage } from '../url';
import { isFullScreen, isTouchDevice } from '../user/device-info';
import { getSaveSearchState } from '../search/state';
import { getSavedProperties } from './storage';
import { isSafari } from '../useragent';
import { newFeatureFilter } from './request-filters';
import { combineCustomStyles } from './styles';
import { buildStyleURL, defaultMapStyleID } from './styles/maptiler';
import { addAllImagesToMap } from './images';
import { addAreaToMap } from './area';
import { coordinatesToFeature } from './geojson';
import { parseCoordinateString } from '../strconv/spatial';
import { urlSearchParamsAsObject } from '../url/search-params';
import { truncateToFixed } from './spatial';
import { whenFeaturesAvailable } from './features';
import { getState } from '../routing/history';
import { getUser } from '../user/user';

import onMapClick from './events/click';
import onMapMove from './events/move';
import { onMapMoveEnd } from './events/move-end';
import onMapMouseOut from './events/mouse-out';
import { detectMapNavigateLinkClick, onMapNavigateLinkClick } from './events/navigate';
import onHistoryPopstate from './events/popstate';
import onBoxEnd from './events/bounding-box';
import onResize from './events/resize';
import onExit from './events/exit';
import { onGeoLocate, onGeoLocateTrigger } from './events/geolocate';
import { onFullscreenChange } from './events/fullscreen';
import { removeArea } from './events/area';
import PropertyResponse from './models/property-response';
import FeatureResponse from './models/feature-response';
import Property from './models/property';
import PropertySearchFilter from '../property/filters';

import {
    boundingBoxIsLikePoint,
    fitBoundsAsync,
    getBoundingBoxFromFlatCoords,
    newBoundingBoxEvent,
    newBoundingBoxOptions,
    validateBoundingBox,
} from './bounding-box';

import {
    handleDistrictLayerClick,
    handleEntityLayerClick,
    handleEventDispatcherLayerClick,
    handleMunicipalityLayerClick,
    handleNeighbourhoodLayerClick,
    handlePropertyLayerClick,
    handleProvinceLayerClick,
    handleRegionLayerClick,
} from './layer-handlers';

import {
    findFirstCustomLayer,
    removeApproxLocationCircle,
    removeLayersForSource,
    setLayer,
} from './layers';

import {
    Coast,
    Country,
    District,
    Entity,
    Island,
    Municipality,
    Neighbourhood,
    Province,
    Region,
    locationTypes,
} from '../locations';

import LocationIndicator from './components/location-indicator';
import Reset from './components/map-reset';
import Notifications from './components/notifications';
import StyleSwitcher from './components/style-switcher';
import SearchThisArea from './components/search-this-area';
import DrawYourSearch from './components/draw-your-search';
import MapQuickLink from './components/quick-link';
import RemoveArea from './components/remove-area';
import FindYourPlace from './components/location-filters';
import InfoPopup from './components/info-popup';
import Logger from '../logging/logger';
import { onEventDispatcherLayerEnter, onEventDispatcherLayerLeave, onLocationLayerEnter, onLocationLayerLeave, onPropertyLayerEnter, onPropertyLayerLeave } from './events/mouse';
import onSavedListUpdate from './events/saved-list-updated';
import onDiscardedListUpdate from './events/discarded-list-updated';
import { triggerPageView } from '../analytics/ga/events';
import { pushEcommerceEvent } from '../analytics/ga/ecommerce';
import { getAllowedCurrentLocationCountries } from './util';
import { captureException } from '../logging/logger';

const TotalPropertiesBeforeFilterWarning = 1000;

/**
 * @param {Element} mapElement the DOM Element to bind the map to
 * @param {Object} options the options for initialising the map
 */
class MapSearch {
    constructor( mapElement, options = {} ) {
        if ( !mapboxgl.supported( {
            failIfMajorPerformanceCaveat: false,
        } ) ) {
            throw new Error( 'browser does not support Mapbox GL' );
        }

        if ( !mapElement ) {
            throw new ReferenceError( 'A valid Element must be provided' );
        }

        const viewport = document.querySelector( '[name="viewport"]' );

        if ( viewport ) {
            viewport.setAttribute( 'content', viewport.getAttribute( 'content' ) + ',maximum-scale=1' );
        }

        this.initialised = false;
        this.language = getLanguage() || 'en';
        this.debug = options.debug || false;
        this.touchEnabled = isTouchDevice();
        this.isFullScreen = isFullScreen();

        Logger.info( '[map] Initialising MapSearch', options );

        this.root = mapElement;
        this.parentElement = mapElement.parentElement;

        this.properties = new Map();
        this.activeComponents = new Set();

        this.movefuncs = [];

        this.options = Object.assign( {}, options );
        this.mapOptions = this.buildMapOptions();
        this.apiURL = new URL( this.options.geoAPI.url );
        this.propertySearchURL = urlWithLanguage( this.options.propertyAPI, '/map-search' );

        this.currentCursorTarget = null;
        this.focusedFeature = null;

        this.firstStateChange = true;

        this.loader = document.querySelector( '#twc__map-search-container .loader' );
        this.isLoading = false;
        this.activeProcesses = 0;

        this.propertyPopup = null;
        this.activeProperty = null;
        this.activeLayer = null;

        this.currentLocation = null;
        this.previousLocation = null;

        this.stringTemplates = this.options.stringTemplates;

        this.locationIndicator = null;

        this.baseForm = document.querySelector( '#property-search-form-base' );
        this.popupForm = document.querySelector( '#property-search-form-popup' );

        if ( !this.baseForm || !this.popupForm ) {
            throw new Error( 'cannot start MapSearch without search forms' );
        }

        this.previousSearchState = null;

        this.initialSearchFilter = PropertySearchFilter.fromURLSearchParams( new URLSearchParams( window.location.search ) );

        this.messageContainer = document.querySelector( '#twc__map-search-messagecontainer' );
        this.locationKey = document.querySelector( '.property-search-form [name=locationId]' );

        this.onMapMoveEndHandler = this.proxyEvent( this, onMapMoveEnd );

        this.userPosition = {
            lat: null,
            lon: null,
        };

        this.ui = new SearchUI( this );

        this.notifications = new Notifications( this );

        this.fullscreen = new mapboxgl.FullscreenControl( {
            container: document.body,
        } );

        this.locationIndicator = new LocationIndicator();

        this.navigation = new mapboxgl.NavigationControl();

        this.scale = new mapboxgl.ScaleControl( {
            maxWidth: 80,
            unit: 'metric',
        } );

        this.reset = new Reset( this );

        this.removeArea = new RemoveArea( this );

        this.infoPopup = new InfoPopup( this );

        this.findYourPlace = new FindYourPlace( this );

        this.drawYourSearch = new DrawYourSearch( this, {
            searchURL: urlWithLanguage( this.options.propertyAPI, '/map-search/your-area' ),
        } );

        this.searchThisArea = new SearchThisArea( this, {
            searchURL: urlWithLanguage( this.options.propertyAPI, '/map-search/this-area' ),
            initialState: {
                active: false,
            },
            eventHandlersToDisable: [
                {
                    name: 'moveend',
                    handler: this.onMapMoveEndHandler,
                },
            ],
        } );

        const styleSwitcherOptions = {
            beforeSetStyle: combineCustomStyles,
            afterSetStyle: ( ctx ) => {
                addAllImagesToMap( ctx.map );
            },
            defaultStyle: {
                id: defaultMapStyleID,
                url: buildStyleURL( defaultMapStyleID ),
                btnHTML: this.getTemplateString( 'styleSwitcherStreet' ),
            },
            altStyle: {
                id: 'satellite',
                url: buildStyleURL( 'hybrid' ),
                btnHTML: this.getTemplateString( 'styleSwitcherSatelite' ),
            },
        };

        this.styleSwitcherMobile = new StyleSwitcher( this, {
            ...styleSwitcherOptions,
            containerClasses: [
                'styleswitcher__container--mobile',
            ],
            btnClasses: [
                'twc__map-search-style-switcher--mobile',
            ],
        } );

        this.styleSwitcherDesktop = new StyleSwitcher( this, {
            ...styleSwitcherOptions,
            containerClasses: [
                'styleswitcher__container--desktop',
            ],
            btnClasses: [
                'twc__map-search-style-switcher--desktop',
            ],
        } );

        this.geoLocate = new mapboxgl.GeolocateControl( {
            positionOptions: {
                enableHighAccuracy: true,
            },
            showAccuracyCircle: false,
        } );

        this.abortController = new AbortController();

        this.map = new mapboxgl.Map( this.mapOptions );

        this.map.addControl( this.geoLocate );

        this.map.on( 'load', this.initMap.bind( this ) );
    }

    /**
     * @returns void
     */
    async initMap() {
        Logger.info( '[map] Map Loaded' );

        const currentURL = new URL( window.location.href );

        const viewMode = currentURL.searchParams.get( 'viewmode' ) || '';

        const currentState = getState();

        if ( viewMode === DrawYourSearch.id || viewMode === 'current-location' ) {
            const urlWithoutViewMode = new URL( currentURL.toString() );
            urlWithoutViewMode.searchParams.delete( 'viewmode' );
            currentState.url = urlWithoutViewMode.toString();
        }

        window.router.Replace( 'current', Object.assign( {}, currentState, {
            manualPopstate: true,
        } ) );

        // taking url from canonical link without numpag if preset - if not taking from current page
        const canonicalElement = document.querySelector( 'link[rel="canonical"]' );
        let baseUrl = canonicalElement ? canonicalElement.href : window.location.href;

        const newURL = new URL( baseUrl );
        newURL.searchParams.delete( 'numpag' );
        newURL.searchParams.set( 'mode', 'map' );
        newURL.searchParams.sort();
        newURL.hash = '';

        const initialState = {
            title: document.title,
            url: newURL.toString(),
            hasFragment: false,
            fragment: '',
            isRoutedPopup: false,
            scrollPosition: 0,
            meta: {
                firstMapView: true,
            },
            manualPopstate: true,
            onlyUpdateState: true,
            stateMode: 'replace',
        };

        if ( !currentURL.searchParams.has( 'mode' ) ) {
            initialState.stateMode = 'push';
        }

        document.dispatchEvent( new CustomEvent( 'twc:manual-navigation', {
            bubbles: true,
            detail: initialState,
        } ) );

        const resizeHandler = this.proxyEvent( this, onResize );

        resizeHandler(); // trigger a resize to ensure height is correct

        this.map.showCollisionBoxes = process.env.MAP_SEARCH_COLLISION_BOXES === 'true' || false;

        this.loader.classList.add( 'loader-hidden' );

        /**
         * Add Initial Source
         */

        const addRegionSource = (
            (
                !this.options.initialLocation ||
                !this.options.initialLocation.id > 0
            ) &&
            this.initialSearchFilter.area.length === 0 &&
            !this.initialSearchFilter.anyLocationFilters()
        );

        if ( addRegionSource ) {
            this.map.addSource( Region, {
                type: 'geojson',
                data: this.JSON[ Region ],
                tolerance: 0,
            } );

            setLayer( this, {
                name: Region,
                source: Region,
            } );
        }

        /**
         * Add Controls
         */
        this.map.addControl( this.locationIndicator );

        let allowFullscreen = true;

        // Due to issue document in TSRWD-2287
        // Safari security feature exiting fullscreen
        if ( isSafari( window.navigator.userAgent ) && isTouchDevice() ) {
            allowFullscreen = false;
        }

        if ( getAllowedCurrentLocationCountries().includes( getUser().visitorInfo().country ) ) {
            Logger.info( 'geolocate control initialised' );

            this.geoLocate.on( 'geolocate', this.proxyEvent( this, onGeoLocate ) );

            this.on( 'twc:mapsearch:geolocate-activate', this.proxyEvent( this, onGeoLocateTrigger ) );

            if ( viewMode === 'current-location' ) {
                this.geoLocate.trigger();
            }
        } else {
            this.map.removeControl( this.geoLocate );
            this.geoLocate = null;
        }

        // Only shown from >= sm
        this.map.addControl( new mapboxgl.AttributionControl(), 'bottom-right' );

        // Only shown for < sm
        this.map.addControl( new mapboxgl.AttributionControl(), 'top-left' );

        if ( allowFullscreen ) {
            this.map.addControl( this.fullscreen, 'top-right' );
        }

        this.map.addControl( this.navigation, 'top-right' );

        this.map.addControl( this.styleSwitcherMobile, 'top-right' );

        this.map.addControl( this.infoPopup, 'top-right' );

        this.map.addControl( this.scale, 'bottom-right' );

        this.reset.init( this.map );

        this.drawYourSearch.init( this.map );

        this.searchThisArea.init( this.map );

        this.map.addControl( this.notifications, 'bottom-right' );

        this.map.addControl( this.styleSwitcherDesktop, 'bottom-right' );

        this.removeArea.init( this.map );

        let canaryIslandQuickLinkOptions = {
            name: 'quick-link-canary-islands',
            imageURL: assetURL( '/images/canary-islands-map.jpg' ),
            text: this.getTemplateString( 'btnCanaryIslands' ),
            bbox: this.locations.spain.bounds.canaryIslands,
            currentLocationID: 5,
            currentLocationType: Region,
            legacyLocationID: 6,
            legacyLocationType: Region,
            hideAfterClick: true,
            featureSearchURL: new URL( `/${Province}`, this.apiURL ),
            featureSearchParams: newFeatureFilter( {
                parentID: 5,
                parentType: Region,
            } ),
            beforeclick: ( ctx ) => {
                if ( ctx.map.getLayer( `${Region}:fill` ) ) {
                    ctx.map.setFilter( `${Region}:fill`, [
                        '!=', [ 'get', 'id' ], 5,
                    ] );
                }

                whenFeaturesAvailable( ctx.map, {
                    source: Province,
                    filter: null,
                    validate: this.debug,
                } ).then( ( features ) => {
                    const islandFeatures = features.filter( ( feature ) => feature.properties.type === Island );

                    for ( let i = 0; i < islandFeatures.length; i++ ) {
                        ctx.map.setFeatureState(
                            {
                                source: Province,
                                id: islandFeatures[ i ].id,
                            },
                            {
                                active: true,
                            } );
                    }
                } );
            },
        };

        this.map.addControl( new MapQuickLink( this, canaryIslandQuickLinkOptions ), 'bottom-left' );

        /**
         * Add Map Images / Property Markers
         */

        try {
            await addAllImagesToMap( this.map );
        } catch ( err ) {
            throw new Error( `failed loading map markers, err: ${err.message}` );
        }

        /**
         * Add Global Map Event Listeners
         */
        this.map.on( 'click', this.proxyEvent( this, onMapClick ) );

        this.map.on( 'mouseout', this.proxyEvent( this, onMapMouseOut ) );

        this.map.on( 'moveend', this.onMapMoveEndHandler );

        this.map.on( 'moveend', this.proxyEvent( this, onMapMove ) );

        this.map.on( 'zoomend', this.proxyEvent( this, onBoxEnd ) );

        this.map.on( 'moveend', this.proxyEvent( this, onBoxEnd ) );

        [
            'radius-close:fill',
            'area-close:fill',
        ].forEach( ( layerName ) => {
            this.map.on( 'mouseenter', layerName, this.proxyEvent( this, onEventDispatcherLayerEnter ) );
            this.map.on( 'mouseleave', layerName, this.proxyEvent( this, onEventDispatcherLayerLeave ) );
        } );

        this.map.on( 'mouseenter', 'properties', this.proxyEvent( this, onPropertyLayerEnter ) );

        this.map.on( 'mouseleave', 'properties', this.proxyEvent( this, onPropertyLayerLeave ) );

        locationTypes.filter( ( locationType ) => {
            return locationType !== Country;
        } ).forEach( ( locationType ) => {
            this.map.on( 'mousemove', `${locationType}:fill`, this.proxyEvent( this, onLocationLayerEnter ) );
            this.map.on( 'mouseleave', `${locationType}:fill`, this.proxyEvent( this, onLocationLayerLeave ) );
        } );

        this.on( `twc:mapsearch:click-${Region}`, this.proxyEvent( this, handleRegionLayerClick ) );

        this.on( `twc:mapsearch:click-${Province}`, this.proxyEvent( this, handleProvinceLayerClick ) );

        this.on( `twc:mapsearch:click-${Island}`, this.proxyEvent( this, handleProvinceLayerClick ) );

        this.on( `twc:mapsearch:click-${Coast}`, this.proxyEvent( this, handleProvinceLayerClick ) );

        this.on( `twc:mapsearch:click-${Municipality}`, this.proxyEvent( this, handleMunicipalityLayerClick ) );

        this.on( `twc:mapsearch:click-${District}`, this.proxyEvent( this, handleDistrictLayerClick ) );

        this.on( `twc:mapsearch:click-${Neighbourhood}`, this.proxyEvent( this, handleNeighbourhoodLayerClick ) );

        this.on( `twc:mapsearch:click-${Entity}`, this.proxyEvent( this, handleEntityLayerClick ) );

        this.on( 'twc:mapsearch:click-property', this.proxyEvent( this, handlePropertyLayerClick ) );

        this.on( 'twc:mapsearch:click-eventDispatcher', this.proxyEvent( this, handleEventDispatcherLayerClick ) );

        this.on( 'twc:mapsearch:click-navigateLink', this.proxyEvent( this, onMapNavigateLinkClick ) );

        this.on( 'twc:mapsearch:remove-radius', this.proxyEvent( this, removeRadius ) );

        this.on( 'twc:mapsearch:remove-area', this.proxyEvent( this, removeArea ) );

        this.on( 'twc:mapsearch:toggle-component', this.toggleComponent.bind( this ) );

        window.addEventListener( 'click', this.proxyEvent( this, detectMapNavigateLinkClick ) );

        window.addEventListener( 'twc:navigation-popstate', this.proxyEvent( this, onHistoryPopstate ) );

        window.addEventListener( 'twc:saved-list-updated', this.proxyEvent( this, onSavedListUpdate ) );

        window.addEventListener( 'twc:discarded-updated', this.proxyEvent( this, onDiscardedListUpdate ) );

        window.addEventListener( 'twc:popup:close', resizeHandler );

        window.addEventListener( 'collapse:collapsed', resizeHandler );

        window.addEventListener( 'twc:popup:close-property-search-filters:after', resizeHandler );

        window.addEventListener( 'twc:popup:close-property-search-provide-name', resizeHandler );

        window.addEventListener( 'resize', resizeHandler );

        window.addEventListener( 'scroll', resizeHandler, { passive: true } );

        const fullscreenEvents = [
            'webkitfullscreenchange',
            'fullscreenchange',
        ];

        for ( let event = 0; event < fullscreenEvents.length; event++ ) {
            document.addEventListener( fullscreenEvents[ event ], this.proxyEvent( this, onFullscreenChange ) );
            document.addEventListener( fullscreenEvents[ event ], resizeHandler );
        }

        this.parentElement.addEventListener( 'mouseleave', this.proxyEvent( this, onExit ) );

        this.baseForm.addEventListener( 'submit', handleSearch( this, this.baseForm, this.popupForm ) );

        this.baseForm.addEventListener( 'twc:mapsearch:form-submit', handleSearch( this, this.baseForm, this.popupForm ) );

        this.popupForm.addEventListener( 'submit', handleSearch( this, this.popupForm, this.baseForm ) );

        if ( 'visualViewport' in window ) {
            window.visualViewport.addEventListener( 'resize', resizeHandler );
        }

        const searchOptions = {
            from: this.baseForm,
            to: this.popupForm,
        };

        const newURLForLinks = new URL( window.location.href );

        newURLForLinks.searchParams.set( 'mode', 'map' );

        updateURLs( '.sidenav__drawer[data-id="currency"] a', 'returnUrl', `${newURLForLinks.pathname}?${newURLForLinks.searchParams.toString()}` );

        updateURLs( '.sidenav__drawer[data-id="language"] a', 'returnUrl', `${newURLForLinks.pathname}?${newURLForLinks.searchParams.toString()}` );

        updateURLs( '.twc__aggregation-link', 'mode', 'map' );

        updateURLs( '.twc__breadcrumb-link', 'mode', 'map' );

        let doSearch = true;

        if ( viewMode === SearchThisArea.id ) {
            this.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:search-this-area:activate', {
                bubbles: true,
                detail: {
                    search: true,
                    triggerStateChange: false,
                    fetchFeatures: this.initialSearchFilter.area.length > 0,
                },
            } ) );

            doSearch = false;
        }

        if ( viewMode === 'current-location' ) {
            doSearch = false;
        }

        if ( this.initialSearchFilter.anyLocationFilters() ) {
            doSearch = false;

            this.baseForm.dataset.initialSearchStarted = 'true';
            this.popupForm.dataset.initialSearchStarted = 'true';

            await this.findYourPlace.search( {
                triggerStateChange: false,
            } );
        }

        if ( doSearch ) {
            await search( this, null, searchOptions );
        }

        if ( viewMode === DrawYourSearch.id ) {
            this.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:draw-your-search:activate', {
                bubbles: true,
            } ) );
        }

        this.parentElement.classList.add( 'twc__map-search--loaded' );

        const initialNotificationID = 'discover-map-search';
        const hasSeenInitialNotificationInSession = window.appStorage.getSessionItem( `mapsearch.notification.${initialNotificationID}.hide` ) === 'true';

        if ( !hasSeenInitialNotificationInSession && viewMode !== DrawYourSearch.id ) {
            this.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:show-notification', {
                bubbles: true,
                detail: {
                    id: initialNotificationID,
                    msg: this.getTemplateString( 'discover' ),
                    options: {
                        autohide: false,
                        btn: ( handler ) => {
                            const hideBtn = document.createElement( 'button' );
                            hideBtn.className = 'twc__map-search-item twc__map-search-item--dark mt-sm';
                            hideBtn.type = 'button';
                            hideBtn.innerHTML = this.getTemplateString( 'discoverClose' );
                            hideBtn.dataset.forNotification = initialNotificationID;

                            hideBtn.addEventListener( 'click', () => {
                                window.appStorage.setSessionItem( `mapsearch.notification.${initialNotificationID}.hide`, 'true' );
                                handler.hide();
                            } );

                            return hideBtn;
                        },
                    },
                },
            } ) );
        }

        this.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:initialised', { bubbles: true } ) );

        this.initialised = true;

        this.setLoading( false );
    }

    toggleComponent( event ) {
        const { componentID, active } = event.detail;

        if ( !componentID ) {
            return;
        }

        const componentClass = `mapsearch__${componentID}--active`;

        if ( active ) {
            this.activeComponents.add( componentID );
            document.documentElement.classList.add( componentClass );
        } else {
            this.activeComponents.delete( componentID );
            document.documentElement.classList.remove( componentClass );
        }
    }

    /**
     * @param {MapSearch} ctx - the MapSearch context to pass
     * @param {Funciton} handler - the event handler function to call
     * @returns void
     */
    proxyEvent( ctx, handler ) {
        handler = handler.bind();

        return function( event ) {
            handler( ctx, event );
        };
    }

    /**
     * @param {String} eventName the name of the event to add a listener to
     * @param {Function} callback the functions to be bound to the event listener
     * @param {Object} options the same options supported by EventTarget.addEventListener
     * @returns void
     */
    on( eventName, callback, options = {} ) {
        const defaults = {
            capture: false,
            once: false,
            passive: true,
        };

        const eventOptions = Object.assign( {}, defaults, options );

        document.addEventListener( eventName, callback, eventOptions );
    }

    /**
     * @param {Object} feature the feature from the map
     * @param {Object} options options for property & feature tasks
     * @param {Object} metaOptions meta options for the update function
     * @returns PromiseSettledResult the result of the promises
     */
    async update( options = {}, metaOptions = {}, abortSignal = null ) {
        const defaultOptions = {
            fetchProperties: false,
            fetchFeatures: false,
            expectProperties: options.fetchProperties ? true : false,
            featureSearchURL: null,
            featureSearchParams: new URLSearchParams(),
            propertySearchURL: null,
            propertySearchParams: new URLSearchParams(),
            propertySearchBody: null,
        };

        const defaultMeta = {
            focusMapToSearch: true,
            appendFeaturesIfLayerExists: false,
            recalculateBboxAfterAnimation: true,
            fillLayer: null,
            outlineLayer: null,
            asyncTasks: [],
            tasks: [],
            filters: [],
            fitBounds: null,
            updateUI: true,
            initialSearch: false,
            triggerStateChange: true,
            useBoundingBoxOf: 0,
            addArea: true,
            addAreaClose: true,
        };

        options = Object.assign( {}, defaultOptions, options );

        metaOptions = Object.assign( {}, defaultMeta, metaOptions );

        Logger.info( '[map] Map Updating', {
            options: options,
            meta: metaOptions,
        } );

        const addProperties = () => {
            let features = [];

            this.properties.forEach( ( p ) => {
                features.push( p.asFeature() );
            } );

            let src = this.map.getSource( 'properties' );

            if ( typeof src === 'undefined' ) {
                this.map.addSource( 'properties', {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: features,
                    },
                } );
            } else {
                src.setData( {
                    type: 'FeatureCollection',
                    features: features,
                } );
            }

            if ( !this.map.getLayer( 'properties' ) ) {
                Logger.info( '[map] Adding Map Layer with ID: properties', options );

                this.map.addLayer( {
                    id: 'properties',
                    type: 'symbol',
                    source: 'properties',
                    layout: {
                        'icon-image': '{icon}',
                        'icon-anchor': 'center',
                        'icon-padding': 0,
                        'icon-ignore-placement': true,
                        'icon-allow-overlap': true,
                        'icon-size': [
                            'interpolate',
                            [ 'exponential', 2 ],
                            [ 'zoom' ],
                            0,
                            0.5,
                            12,
                            1,
                        ],
                        'visibility': 'visible', // possible values = visible || none
                    },
                } );

                this.map.on( 'mouseenter', 'properties', () => {
                    this.map.getCanvas().style.cursor = 'pointer';
                } );

                this.map.on( 'mouseleave', 'properties', () => {
                    this.map.getCanvas().style.cursor = '';
                } );
            }

            this.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:properties-added', {
                bubbles: true,
            } ) );
        };

        const validateOptions = () => {
            if ( options.fetchFeatures && options.featureSearchURL === null ) {
                return new Error( 'cannot update, featureSearchURL is null' );
            }

            if ( options.featureSearchURL !== null ) {
                let err = validateURLString( options.featureSearchURL.toString() );

                if ( err !== null ) {
                    return err;
                }
            }

            if ( options.fetchProperties && options.propertySearchURL === null ) {
                return new Error( 'cannot update, propertySearchURL is null' );
            }

            if ( options.propertySearchURL !== null ) {
                let urlErr = validateURLString( options.propertySearchURL.toString() );

                if ( urlErr !== null ) {
                    return urlErr;
                }
            }

            return null;
        };

        const fetchFeatures = async () => {
            let url = options.featureSearchURL.toString();

            if ( options.featureSearchParams ) {
                url += '?' + options.featureSearchParams.toString();
            }

            const response = await http( url, {
                signal: abortSignal,
            } );

            if ( response.status === 204 ) {
                return featureResponseStub;
            }

            const body = await response.json();

            let featureResponse = new FeatureResponse( body, options );

            if ( featureResponse.type ) {
                if ( !metaOptions.appendFeaturesIfLayerExists ) {
                    removeLayersForSource( this.map, featureResponse.type );

                    if ( this.map.getSource( featureResponse.type ) ) {
                        this.map.removeSource( featureResponse.type );
                    }

                    Logger.info( '[map] Adding Map Source with ID: ' + featureResponse.type );

                    this.map.addSource( featureResponse.type, {
                        type: 'geojson',
                        data: body,
                        tolerance: 0, // all simplification is done on the server
                    } );

                    setLayer( this, {
                        source: featureResponse.type,
                        name: featureResponse.type,
                        fillLayer: metaOptions.fillLayer,
                        outlineLayer: metaOptions.outlineLayer,
                        filters: metaOptions.filters,
                    } );
                }

                if ( metaOptions.appendFeaturesIfLayerExists ) {
                    let src = this.map.getSource( featureResponse.type );

                    if ( src ) {
                        let currentFeatures = src.serialize().data.features || [];

                        let currentFeaturesIDs = currentFeatures.map( ( feature ) => {
                            return feature.id;
                        } );

                        let newFeatures = ( body.features || [] ).filter( ( feature ) => {
                            return !currentFeaturesIDs.includes( feature.id );
                        } );

                        let geoJSONMerged = [ ...currentFeatures, ...newFeatures ];

                        Logger.info( '[map] Updating Map Source with ID: ' + featureResponse.type );

                        src.setData( {
                            type: 'FeatureCollection',
                            features: geoJSONMerged,
                        } );
                    } else {
                        this.map.addSource( featureResponse.type, {
                            type: 'geojson',
                            data: body,
                            tolerance: 0, // all simplification is done on the server
                        } );

                        setLayer( this, {
                            source: featureResponse.type,
                            name: featureResponse.type,
                            fillLayer: metaOptions.fillLayer,
                            outlineLayer: metaOptions.outlineLayer,
                            filters: metaOptions.filters,
                        } );
                    }
                }
            }

            if ( featureResponse.bbox && boundingBoxIsLikePoint( featureResponse.bbox ) ) {
                removeApproxLocationCircle( this.map );

                let circleFeature = circle( featureResponse.bbox[ 0 ], 0.5, {
                    units: 'kilometers',
                } );

                this.map.addSource( 'approx-location', {
                    type: 'geojson',
                    data: circleFeature,
                    tolerance: 0,
                } );

                this.map.addLayer( {
                    id: 'approx-location:fill',
                    source: 'approx-location',
                    type: 'fill',
                    paint: {
                        'fill-color': '#FFDE00',
                        'fill-opacity': 0.5,
                    },
                }, findFirstCustomLayer( this.map ) );
            }

            this.activeLayer = featureResponse.type;

            return featureResponse;
        };

        const fetchProperties = async () => {
            let url = options.propertySearchURL.toString();

            if ( !options.propertySearchParams ) {
                options.propertySearchParams = new URLSearchParams();
            }

            // options.propertySearchParams.set( 'take', 200 );

            const response = await http( url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: options.propertySearchParams.toString(),
                signal: abortSignal,
            } );

            if ( response.status !== 200 ) {
                return;
            }

            const body = await response.json();

            let propertyResponse = new PropertyResponse( body );

            let errorResponse = body.error && body.error.length > 0;

            if ( errorResponse ) {
                this.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:show-notification', {
                    bubbles: true,
                    detail: {
                        msg: body.error,
                        options: {
                            autohide: true,
                            autohideAfter: 10000,
                        },
                    },
                } ) );

                return propertyResponse;
            }

            if ( metaOptions.updateUI ) {
                let updateContent = true;

                let dataToUpdate = {
                    ...body,
                };

                if ( options.propertySearchParams.get( 'onlyProperties' ) === 'true' || this.activeComponents.has( SearchThisArea.id ) ) {
                    updateContent = false;

                    dataToUpdate = {
                        ...dataToUpdate,
                        total: body.total,
                    };
                }

                document.dispatchEvent( new CustomEvent( 'twc:search-ui-update', {
                    bubbles: true,
                    detail: {
                        initialSearch: metaOptions.initialSearch,
                        updateContent,
                        data: {
                            ...dataToUpdate,
                        },
                    },
                } ) );
            }

            if ( body.searchURLs && body.searchURLs[ this.language ] ) {
                const newURL = new URL( window.location.origin + body.searchURLs[ this.language ] );
                newURL.searchParams.set( 'mode', 'map' );

                let triggerStateChange = true;

                const currentState = getState() || {};

                let searchParams = urlSearchParamsAsObject( options.propertySearchParams );

                if ( this.firstStateChange ) {
                    this.firstStateChange = false;
                    triggerStateChange = false;

                    let state = {
                        title: document.title,
                        hasFragment: false,
                        fragment: '',
                        isRoutedPopup: false,
                        meta: {
                            ...currentState.meta || {},
                            firstMapView: false,
                            parentLocations: options.propertySearchParams.get( 'parentLocations' ),
                            propertySearchParams: searchParams,
                            saveState: getSaveSearchState( 'property-search' ),
                            location: body.location,
                        },
                        manualPopstate: true,
                    };

                    if ( this.activeComponents.has( SearchThisArea.id ) ) {
                        state.meta.viewMode = SearchThisArea.id;
                    }

                    window.router.Replace( 'current', Object.assign( {}, currentState, state ) );
                }

                if ( triggerStateChange && metaOptions.triggerStateChange ) {
                    let state = {
                        url: newURL.toString(),
                        title: body.title,
                        description: body.description,
                        hasFragment: false,
                        fragment: '',
                        isRoutedPopup: false,
                        scrollPosition: 0,
                        meta: {
                            firstMapView: false,
                            propertySearchParams: searchParams,
                            saveState: getSaveSearchState( 'property-search' ),
                            location: body.location,
                        },
                        manualPopstate: true,
                        onlyUpdateState: true,
                    };

                    if ( this.activeComponents.has( SearchThisArea.id ) ) {
                        state.meta.viewMode = SearchThisArea.id;
                    }

                    document.dispatchEvent( new CustomEvent( 'twc:manual-navigation', {
                        bubbles: true,
                        detail: state,
                    } ) );
                }
            }

            const previouSearchParams = new URLSearchParams( this.previousSearchState );

            const previousSearch = PropertySearchFilter.fromURLSearchParams( previouSearchParams );
            const currentSearch = PropertySearchFilter.fromURLSearchParams( options.propertySearchParams );

            if ( body.contentGroup && body.contentGroup.length > 0 ) {
                window.contentGroup = body.contentGroup;
            }

            let previousMode = '';
            let emitEvent = true;
            let excludeParams = [];

            if ( window.router.previousState.meta && window.router.previousState.meta.viewMode ) {
                previousMode = window.router.previousState.meta.viewMode;
            }

            if ( this.activeComponents.has( SearchThisArea.id ) && previousMode === SearchThisArea.id ) {
                excludeParams.push( 'area' );
            } else if ( this.activeComponents.has( SearchThisArea.id ) ) {
                emitEvent = false;
            }

            const searchesAreEqual = PropertySearchFilter.equal( previousSearch, currentSearch, excludeParams );

            if ( !searchesAreEqual && emitEvent ) {
                triggerPageView();
            }

            if ( ( !body.properties || body.properties.length === 0 ) && options.expectProperties ) {
                this.properties.forEach( ( property ) => property.destroy() );
                this.properties.clear();

                if ( this.map.getLayer( 'properties' ) ) {
                    this.map.removeLayer( 'properties' );
                    this.map.removeSource( 'properties' );
                }

                return propertyResponse;
            }

            if ( body.properties && body.properties.length >= TotalPropertiesBeforeFilterWarning && options.expectProperties ) {
                this.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:show-notification', {
                    bubbles: true,
                    detail: {
                        msg: this.getTemplateString( 'filterSearchMsg', [ TotalPropertiesBeforeFilterWarning ] ),
                        options: {
                            autohide: true,
                            autohideAfter: 5000,
                        },
                    },
                } ) );
            }

            const dispatchViewItemList = (
                (
                    (
                        metaOptions.initialSearch
                    ) ||
                    (
                        previouSearchParams.get( 'currentLocationID' ) !== options.propertySearchParams.get( 'currentLocationID' )
                    )
                ) &&
                options.propertySearchParams.get( 'offer' ) === 'for-sale'
            );

            if ( dispatchViewItemList && body.viewItemListEvent ) {
                pushEcommerceEvent( JSON.parse( body.viewItemListEvent ) );
            }

            this.activeProperty = null;

            if ( !body.properties ) {
                return propertyResponse;
            }

            const newIDs = body.properties.map( ( property ) => {
                return property.id;
            } );

            let wouldBeDuplicate = [];

            this.properties.forEach( ( property ) => {
                if ( newIDs.includes( property.id ) ) {
                    wouldBeDuplicate.push( property.id );
                    return;
                }
                property.destroy();
                this.properties.delete( property.id );
            } );

            let properties = body.properties.filter( ( property ) => {
                return wouldBeDuplicate.includes( property.id ) === false;
            } );

            const savedProperties = getSavedProperties();

            properties = properties.map( ( property ) => {
                let p = new Property( property, this );

                if ( savedProperties[ p.id ] ) {
                    p.saved = true;
                    p.contacted = savedProperties[ p.id ].contacted;
                }

                return p;
            } );

            for ( let i = 0; i < properties.length; i++ ) {
                this.properties.set( properties[ i ].id, properties[ i ] );
            }

            addProperties();

            return propertyResponse;
        };

        let asyncTasks = [];

        if ( options.fetchFeatures ) {
            asyncTasks.push( fetchFeatures );
        }

        if ( metaOptions.asyncTasks.length > 0 ) {
            for ( let i = 0; i < metaOptions.asyncTasks.length; i++ ) {
                asyncTasks.push( metaOptions.asyncTasks[ i ] );
            }
        }

        let err = validateOptions();

        if ( err ) {
            throw err;
        }

        let results = [];

        for ( let t = 0; t < asyncTasks.length; t++ ) {
            const task = asyncTasks[ t ];
            const result = await task();
            results = [ ...results, result ];
        }

        const featureResponse = results.find( ( result ) => {
            return result instanceof FeatureResponse;
        } );

        let isFittingBounds = false;

        if ( metaOptions.fitBounds && metaOptions.fitBounds.bbox && metaOptions.focusMapToSearch ) {
            isFittingBounds = true;
            await fitBoundsAsync( this, {
                bbox: metaOptions.fitBounds.bbox,
                options: metaOptions.fitBounds.options,
                event: metaOptions.fitBounds.event,
            } );
        }

        if ( metaOptions.addArea && options.propertySearchParams && options.propertySearchParams.has( 'area' ) && !isFittingBounds ) {
            const areaParsed = parseCoordinateString( options.propertySearchParams.get( 'area' ) );

            const feature = coordinatesToFeature( areaParsed );

            this.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:draw-your-search:deactivate', {
                bubbles: true,
                detail: {
                    search: false,
                },
            } ) );

            addAreaToMap( this, feature, {
                addClose: metaOptions.addAreaClose,
            } );

            const areaBboxOptions = {
                padding: {
                    top: 50,
                    right: 50,
                    bottom: 50,
                    left: 50,
                },
            };

            const bboxEvent = newBoundingBoxEvent();

            await fitBoundsAsync( this, {
                bbox: feature.bbox,
                options: areaBboxOptions,
                event: bboxEvent,
            } );
        }

        const hasLonLat = ( options.propertySearchParams && options.propertySearchParams.has( 'latitude' ) && options.propertySearchParams.has( 'longitude' ) );

        if ( options.propertySearchParams && options.propertySearchParams.has( 'radius' ) && hasLonLat && !isFittingBounds ) {
            isFittingBounds = true;

            const radius = options.propertySearchParams.get( 'radius' );

            const center = [
                options.propertySearchParams.get( 'longitude' ),
                options.propertySearchParams.get( 'latitude' ),
            ];

            const radiusCircle = addRadiusToMap( this, {
                center: center,
                radius: radius,
            } );

            const radiusBbox = getBoundingBoxFromFlatCoords( radiusCircle.geometry.coordinates[ 0 ] );

            const radiusBboxOptions = {
                padding: {
                    top: 16,
                    right: 16,
                    bottom: 16,
                    left: 16,
                },
            };

            const bboxEvent = newBoundingBoxEvent( {
                isRadiusCircle: true,
            } );

            await fitBoundsAsync( this, {
                bbox: radiusBbox,
                options: radiusBboxOptions,
                event: bboxEvent,
            } );
        }

        if ( featureResponse && featureResponse.bbox && metaOptions.focusMapToSearch && !isFittingBounds ) {
            isFittingBounds = true;

            let bbox;

            const boundingBoxTarget = metaOptions.useBoundingBoxOf;

            if ( boundingBoxTarget > 0 ) {
                bbox = featureResponse.getBoundingBoxForFeature( parseInt( boundingBoxTarget ) );
            }

            if ( !bbox ) {
                bbox = featureResponse.bbox;
            }

            const err = validateBoundingBox( bbox );

            if ( err ) {
                throw err;
            }

            let bboxOptions = newBoundingBoxOptions();

            if ( boundingBoxIsLikePoint( bbox ) && MaxZoomLevels[ featureResponse.type ] ) {
                bboxOptions.maxZoom = MaxZoomLevels[ featureResponse.type ];
            }

            if ( metaOptions.fitBounds && metaOptions.fitBounds.options ) {
                bboxOptions = Object.assign( {}, bboxOptions, metaOptions.fitBounds.options );
            }

            await fitBoundsAsync( this, {
                bbox: bbox,
                options: bboxOptions,
                event: newBoundingBoxEvent(),
            } );
        }

        if ( options.propertySearchParams && metaOptions.recalculateBboxAfterAnimation ) {
            const bounds = this.map.getBounds();
            options.propertySearchParams.set( 'boundSW', truncateToFixed( bounds.getSouthWest().lat ).toString() + ',' + truncateToFixed( bounds.getSouthWest().lng ).toString() );
            options.propertySearchParams.set( 'boundNE', truncateToFixed( bounds.getNorthEast().lat ).toString() + ',' + truncateToFixed( bounds.getNorthEast().lng ).toString() );
            // options.propertySearchParams.set( 'take', defaultPropertyFilters.take );
        }

        if ( options.fetchProperties ) {
            const propertyResult = await fetchProperties();

            if ( propertyResult instanceof Error ) {
                this.setLoading( false );

                this.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:show-notification', {
                    bubbles: true,
                    detail: {
                        msg: this.getTemplateString( 'genericError' ),
                        options: {
                            autohide: true,
                            autohideAfter: 10000,
                        },
                    },
                } ) );

                captureException( propertyResult );
            }

            results = [ ...results, propertyResult ];
        }

        this.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:updated', {
            bubbles: true,
            detail: {
                options: options,
                metaOptions: metaOptions,
            },
        } ) );

        return results;
    }

    /**
     * @param {Boolean} loading a boolean indicating whether to set the state to loading or not
     * @returns void
     */
    setLoading( loading ) {
        if ( this.isLoading === loading ) {
            return;
        }

        this.isLoading = loading;

        if ( loading === true ) {
            this.activeProcesses = this.activeProcesses + 1;
        }

        if ( loading === false ) {
            this.activeProcesses = this.activeProcesses - 1;
        }

        if ( this.activeProcesses === 0 ) {
            this.loader.classList.add( 'loader-hidden' );
            this.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:stop-loading', { bubbles: true } ) );
        }

        if ( this.activeProcesses > 0 ) {
            this.loader.classList.remove( 'loader-hidden' );
            this.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:start-loading', { bubbles: true } ) );
        }
    }

    /**
     * @param {String} templateName the name of the template to use, use syntax "{number}" for variables
     * @param {Array} values an array template paremeters to use within the template
     * @returns the template string itself
     * @example
     * // Simple template
     * // templatePlateName = "I can count to 1 2 3"
     * var str = getTemplateString( 'templatePlateName', [] ); // output is the same as the input
     *
     * // Template with variabless
     * // templatePlateName = "I can count to 1 2 {0}"
     * var str = getTemplateString( 'templatePlateName', [ 3 ] ); // output is the same as the input with {0} replace with 3
     */
    getTemplateString( templateName, values ) {
        if ( typeof values === 'undefined' ) {
            return this.stringTemplates[ templateName ];
        }
        return this.stringTemplates[ templateName ].replace( /{(\d+)}/g, ( match, number ) => {
            return typeof values[ number ] !== 'undefined' ? values[ number ] : match;
        } );
    }

    /**
     * buildMapOptions builds the map options using the defaults & user-defined options
     * @returns a object containing all options for the map
     */
    buildMapOptions() {
        const options = Object.assign( {}, this.defaultOptions() );

        options.container = this.root;
        options.style = buildStyleURL( defaultMapStyleID );

        if ( this.options.initialLocation.id > 0 ) {
            options.bounds = this.options.initialLocation.bbox;
        }

        if ( this.options.bbox ) {
            options.bounds = [
                [
                    this.options.bbox.northEast.lon,
                    this.options.bbox.northEast.lat,
                ],
                [
                    this.options.bbox.southWest.lon,
                    this.options.bbox.southWest.lat,
                ],
            ];
        }

        if ( this.options.bboxOptions ) {
            options.fitBoundsOptions = this.options.bboxOptions;
        }

        if ( boundingBoxIsLikePoint( options.bounds ) && this.options.initialLocation.id > 0 ) {
            options.fitBoundsOptions = {
                maxZoom: MaxZoomLevels[ this.options.initialLocation.type ],
            };
        }

        return options;
    }

    /**
     * @returns the default options for the map
     */
    defaultOptions() {
        return {
            hash: false,
            failIfMajorPerformanceCaveat: !process.env.DEBUG,
            preserveDrawingBuffer: true,
            scrollZoom: true,
            dragRotate: true,
            touchZoomRotate: true,
            pitchWithRotate: false,
            touchPitch: false,
            interactive: true,
            dragPan: true,
            doubleClickZoom: true,
            bounds: this.locations.spain.bounds.mainland,
            maxBounds: this.locations.spain.bounds.allWithPadding,
            attributionControl: false,
        };
    }

    get JSON() {
        return {
            [ Region ]: window.regionsGeoJSON,
        };
    }

    get locations() {
        return {
            spain: {
                bounds: {
                    mainland: [
                        [ -14.595104766383656, 35.391245230422896 ],
                        [ 8.183799299894645, 44.143571373876114 ],
                    ],
                    all: [ // Mainland Spain, Canary Islands, Balearic Islands, Melilla, Ceuta
                        [ -26.613302622868105, 27.473585445287597 ],
                        [ 17.720095917976664, 43.810188804616644 ],
                    ],
                    allWithPadding: [
                        [ -62.76144387468062, 11.729545276563599 ],
                        [ 49.09376735980385, 56.074210265281465 ],
                    ],
                    canaryIslands: [
                        [ -18.161180849999397, 27.558040652618246 ],
                        [ -13.334958359999035, 29.494741931235225 ],
                    ],
                },
            },
        };
    }
}

export default MapSearch;
